import { getHashObj } from "@/utils/utils"
import responsesCache from "@/vue/planning/state/responsesCache"
import getCacheKey from "../../logic/getCacheKey"
import transportPackCode from "@/config/transportPackCode"
import flightPackCode from "@/config/flightPackCode"
import _ from "lodash"
import trainPackCode from "@/config/trainPackCode"
import getSiteFromDomain from "../../logic/getSiteFromDomain"
import { trainStationCodeMap } from "@/config/trainStationCodeMap"

type PlanningState = {
	// this is used by the green thing
	packCode: string
	seAvailabilities: { [key: string]: any } | null
	prices: { [key: string]: any } | null
	selectedPrestations: any
	selectedDates: any
	loading: boolean
	language: string
	cheapestPrestation: { [key: string]: any } | null
	cheapestPrice: number | null
	wiypCheapestPrice: boolean
	productUnavailable: boolean
	participants: Array<number>
	skipass: boolean
}

export default {
	refreshDataStoredOnWindow: (storeObj: any) => {
		const { state, getters } = storeObj
		const country = getSiteFromDomain(new URL(window.location.href).hostname);
		const trainOrigin = trainPackCode.includes(state.packCode)
			? Array.from(trainStationCodeMap.keys())[0]
			: 'NA';
		const isFlightPackage = flightPackCode.includes(state.packCode)
		const isTrainPackage = trainPackCode.includes(state.packCode)
		const origin = isFlightPackage
			? state.flightOrigin ?? state.wiypDefaultFlightOrigin
			: (isTrainPackage ? (state.flightOrigin?.split("-")[0] || trainOrigin) : 'NA');

		// We need to extract variants from the store
		// Extract all variants, flatten, and get unique prestId values
		const uniquePrestIds = Array.from(
			new Set(
				state.liheData.availablePrestations
					.flatMap((prestation: any) =>
						(prestation.variants || []).map((variant: any) => variant.prestId)
					)
			)
		);

		// Redo logic to find the cheapest price from uniquePrestIds

		let cheapestPrice: number | null = null;
		let foundSomeWiyp = false;

		for (const prestId of uniquePrestIds) {
			const packageId = state.packCode === 'HS' ? `${state.liheData.id}HS` : prestId;
			const wiypData = getters.getWiypCallByFamilyCompositionAndOriginAndPackageCodeAndId(
				state.participants.join('-'),
				origin,
				state.packCode,
				packageId
			);
			if (wiypData && wiypData.wiypPrices && wiypData.wiypPrices.length > 0) {
				foundSomeWiyp = true;
				for (const wiypPrice of wiypData.wiypPrices) {
					if (
						typeof wiypPrice.sellingPriceWiyp === "number" &&
						(cheapestPrice === null || wiypPrice.sellingPriceWiyp < cheapestPrice)
					) {
						cheapestPrice = wiypPrice.sellingPriceWiyp;
					}
				}
			}
		}

		const cacheKey = getCacheKey(
			state.packCode,
			state.dates.BeginDate,
			state.dates.EndDate,
			state.season,
			state.participants,
			state.skipass
		)
		const availResponse = responsesCache.value.getResponse(
			"availabilities",
			cacheKey
		)
		const priceResponse = responsesCache.value.getResponse("prices", cacheKey)

		const newPlanningState: PlanningState = {
			packCode: state.packCode,
			seAvailabilities: availResponse ? availResponse : null,
			prices: availResponse ? priceResponse : null,
			selectedPrestations: availResponse ? getters.selectedPrestations : null,
			selectedDates: state.dates,
			loading: state.loading,
			language: state.language,
			cheapestPrestation: availResponse
				? getters.prestations[getters.cheapestPrestationIndex]
				: null,
			cheapestPrice: cheapestPrice,
			wiypCheapestPrice: foundSomeWiyp && cheapestPrice !== null,
			productUnavailable: availResponse ? state.productUnavailable : null,
			participants: state.participants,
			skipass: state.skipass,
		}

		// Handle async nature if we didn't find any WIYP prices yet: retry for each uniquePrestId
		if (cheapestPrice == null && uniquePrestIds.length > 0) {
			const startTime = Date.now();
			const maxTime = 5000; // 5 seconds
			const interval = 100; // 100 ms

			const checkWiypData = () => {
				let foundCheapest: number | null = null;
				let anyWiyp = false;
				for (const prestId of uniquePrestIds) {
					const packageId = state.packCode === 'HS' ? `${state.liheData.id}HS` : prestId;
					const wiypDataCheck = getters.getWiypCallByFamilyCompositionAndOriginAndPackageCodeAndId(
						state.participants.join('-'),
						state.flightOrigin ?? state.wiypDefaultFlightOrigin ?? trainOrigin,
						state.packCode,
						packageId
					);
					if (wiypDataCheck && wiypDataCheck.wiypPrices && wiypDataCheck.wiypPrices.length > 0) {
						anyWiyp = true;
						for (const wiypPrice of wiypDataCheck.wiypPrices) {
							if (
								typeof wiypPrice.sellingPriceWiyp === "number" &&
								(foundCheapest === null || wiypPrice.sellingPriceWiyp < foundCheapest)
							) {
								foundCheapest = wiypPrice.sellingPriceWiyp;
							}
						}
					}
				}
				if (anyWiyp && foundCheapest !== null) {
					newPlanningState.cheapestPrice = foundCheapest;
					newPlanningState.wiypCheapestPrice = true;
					window.planningState = newPlanningState;
					window.dispatchEvent(new Event("planningStateRefreshed"));
					return;
				}
				if (Date.now() - startTime < maxTime) {
					setTimeout(checkWiypData, interval);
				}
			};
			checkWiypData();
		}
				
		window.planningState = newPlanningState
		window.dispatchEvent(new Event("planningStateRefreshed"))	

	},

	ensurePrestationsPreselected: async (storeObj: any) => {
		const { state, getters, commit, dispatch } = storeObj

		const checkedNeedToPreselectPrestations =
			state.checkedNeedToPreselectPrestations
		const liheData = state.liheData

		if (checkedNeedToPreselectPrestations) return

		// retrieve selection from last time visited page during same session
		const selectedPrestIds_fromPreviousActivity_JSONstring =
			window.sessionStorage.getItem(`selectedPrestIds-${liheData.id}`)

		if (selectedPrestIds_fromPreviousActivity_JSONstring) {
			// preselect from previous activity (stored in sessionStorage)
			const selectedPrestIds_fromPreviousActivity = JSON.parse(
				selectedPrestIds_fromPreviousActivity_JSONstring
			)
			dispatch("setSelectedPrestIds", selectedPrestIds_fromPreviousActivity)
		} else {
			// preselect from hash if no previous activity recorded
			const hash = window.location.hash.replace("#", "")
			const hashObj: { [key: string]: any } = getHashObj(hash)
			const prest_id_source = hashObj.prest_id_source

			if (prest_id_source) {
				
				// Wait for variants to be loaded
				let attempts = 0;
				const maxAttempts = 10;
				
				while (attempts < maxAttempts) {
					const variants = getters.variants;
					
					if (variants && variants.length > 0) {
						// Find all variants that match this prest_id_source
						const matchingVariants = variants.filter(
							(variant: any) => variant.prestIdSource === prest_id_source
						);


						if (matchingVariants.length > 0) {
							// Select the first matching variant
							const variant = matchingVariants[0];
							
							const obj: { [key: string]: any } = {};
							obj[variant.id] = 1;
							obj["scrollToSummary"] = true;

							// If it's a skipass variant, also set the skipass duration
							if (variant.skipass) {
								const daysAmount = `days${variant.skipass}`;
								commit("setSkiPassDays", { daysAmount });
							}

							// Handle transport selection
							if (variant.packCode) {
								// Check if this is a transport pack using the proper config
								const isTransportPack = [...flightPackCode, ...transportPackCode].includes(variant.packCode);
								commit("setTransport", isTransportPack);
								
								// Set the package regardless of transport or not
								dispatch("setPackage", variant.packCode);
							}

							dispatch("setSelectedPrestIds", obj);
							break;
						}
						break;
					}
					
					// Wait 100ms before next attempt
					await new Promise(resolve => setTimeout(resolve, 100));
					attempts++;
				}
				
				if (attempts >= maxAttempts) {
					console.warn('Timeout waiting for variants to load for prest_id_source:', prest_id_source);
				}
			}
		}
		commit("setCheckedNeedToPreselectPrestations", true)
	},

	toggleTransport: (storeObj: any) => {
		const { state, commit } = storeObj

		const newBool = !state.transportCheckbox
		commit("setTransport", newBool)
	},
}
